import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/cx';
import styles from './Button.module.css';

type Variant = 'primary' | 'secondary' | 'outline' | 'accent' | 'onInk' | 'onInkOutline' | 'danger';

interface CommonProps {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  /** Shows an inline spinner and blocks interaction. */
  busy?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

function classesFor({ variant = 'primary', size = 'md', block, className }: CommonProps): string {
  return cx(styles.base, styles[variant], styles[size], block && styles.block, className);
}

export function Button({
  variant = 'primary',
  size = 'md',
  block,
  busy = false,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      className={classesFor({ variant, size, block, className, children })}
      disabled={disabled ?? busy}
      aria-busy={busy || undefined}
    >
      {busy && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** A link that looks like a button. Same visual contract, correct semantics. */
export function ButtonLink({
  to,
  variant = 'primary',
  size = 'md',
  block,
  children,
  className,
}: CommonProps & { to: string }) {
  return (
    <Link to={to} className={classesFor({ variant, size, block, className, children })}>
      {children}
    </Link>
  );
}
